import { get } from '@/lib/api';
import { getDistinctPacketStatusLabel } from '@/lib/packetStatus';
import {
  AssessmentStatus,
  AssessmentType,
  Decision,
  PacketStatus,
  Ratification,
  type Assessment,
  type Concern,
  type Criterion,
  type Endorsement,
  type Invitation,
  type PacketWithCandidate,
  type User,
  parseNameFromEmail,
} from '@promo/shared';
import remarkGfm from 'remark-gfm';
import remarkParse from 'remark-parse';
import { unified } from 'unified';
import type {
  CanvasElement,
  Content,
  ContentText,
  Decoration,
  TableCell,
  TDocumentDefinitions,
  TVirtualFileSystem,
} from 'pdfmake/interfaces';

interface ExportSection<T> {
  data: T;
  error?: string;
}

export interface PacketPdfExportData {
  packet: PacketWithCandidate;
  criteria: ExportSection<Criterion[]>;
  endorsements: ExportSection<Endorsement[]>;
  concerns: ExportSection<Concern[]>;
  invitations: ExportSection<Invitation[]>;
  blindedAssessments: ExportSection<Assessment[]>;
  unblindedAssessments: ExportSection<Assessment[]>;
  barRaiserAssessments: ExportSection<Assessment[]>;
  previousAttempts: ExportSection<PacketWithCandidate[]>;
  relatedUsers: Record<string, User>;
  userLoadErrors: string[];
}

interface ExportPacketPdfOptions {
  downloadPdf?: (documentDefinition: TDocumentDefinitions, filename: string) => Promise<void> | void;
  now?: Date;
}

type RelatedItem = Assessment | Concern | Endorsement | Invitation;
type UserLoadResult = { userId: string; user: User } | { userId: string; error: string };
type DefinitionItem = [label: string, value: string | number | null | undefined];
type PdfMakeModule = typeof import('pdfmake/build/pdfmake');
type PdfMakeBuildModule = PdfMakeModule | { default: PdfMakeModule };
type PdfMakeVfsModule = { default?: TVirtualFileSystem } & Partial<TVirtualFileSystem>;
type MarkdownNode = {
  type: string;
  value?: string;
  children?: MarkdownNode[];
  depth?: number;
  ordered?: boolean;
  start?: number;
  checked?: boolean | null;
  url?: string;
};
type MarkdownMarks = {
  background?: string;
  bold?: boolean;
  color?: string;
  decoration?: Decoration | Decoration[];
  italics?: boolean;
  link?: string;
  style?: string | string[];
};

const DECISION_CHART_WIDTH = 240;
const DECISION_CHART_HEIGHT = 16;
const PROMOTE_COLOR = '#2563eb';
const DEFER_COLOR = '#f59e0b';
const CHART_BACKGROUND_COLOR = '#e5e7eb';
const MARKDOWN_TABLE_CELL_MARGIN: [number, number, number, number] = [0, 3, 0, 3];
const markdownProcessor = unified().use(remarkParse).use(remarkGfm);

let pdfMakePromise: Promise<PdfMakeModule> | null = null;

function getPdfMakeModule(pdfMakeModule: PdfMakeBuildModule): PdfMakeModule {
  return 'default' in pdfMakeModule ? pdfMakeModule.default : pdfMakeModule;
}

function getPdfMakeVfs(vfsModule: PdfMakeVfsModule): TVirtualFileSystem {
  return vfsModule.default ?? (vfsModule as TVirtualFileSystem);
}

function loadPdfMake(): Promise<PdfMakeModule> {
  if (!pdfMakePromise) {
    pdfMakePromise = Promise.all([
      import('pdfmake/build/pdfmake'),
      import('pdfmake/build/vfs_fonts'),
    ]).then(([pdfMakeModule, vfsModule]) => {
      const pdfMake = getPdfMakeModule(pdfMakeModule as PdfMakeBuildModule);
      pdfMake.addVirtualFileSystem(getPdfMakeVfs(vfsModule as PdfMakeVfsModule));
      return pdfMake;
    });
  }

  return pdfMakePromise;
}

function isNetworkFetchError(error: unknown): boolean {
  return error instanceof TypeError && /failed to fetch|load failed|networkerror/i.test(error.message);
}

function formatEnum(value: string | null | undefined): string {
  if (!value) return 'Pending';

  return value
    .split('_')
    .map((word) => (word === 'HR' ? word : word.charAt(0) + word.slice(1).toLowerCase()))
    .join(' ');
}

function formatDate(value: Date | string | null | undefined): string {
  if (!value) return 'Not set';

  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return 'Not set';

  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function formatDateTime(value: Date | string | null | undefined): string {
  if (!value) return 'Not set';

  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return 'Not set';

  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function textValue(value: string | number | boolean | null | undefined, fallback = 'Not set'): string {
  const text = String(value ?? '').trim();
  return text || fallback;
}

function hasText(value: string | null | undefined): boolean {
  return Boolean(value?.trim());
}

function markdownTree(value: string): MarkdownNode {
  return markdownProcessor.parse(value) as MarkdownNode;
}

function mergeMarks(base: MarkdownMarks, next: MarkdownMarks): MarkdownMarks {
  const decorations = [base.decoration, next.decoration]
    .flat()
    .filter((decoration): decoration is Decoration => Boolean(decoration));

  return {
    ...base,
    ...next,
    decoration: decorations.length > 1 ? [...new Set(decorations)] : decorations[0],
  };
}

function textRun(value: string, marks: MarkdownMarks = {}): Content {
  if (!value) return '';

  return Object.keys(marks).length === 0
    ? value
    : {
        text: value,
        ...marks,
      };
}

function renderMarkdownInline(nodes: MarkdownNode[] = [], marks: MarkdownMarks = {}): Content[] {
  return nodes.flatMap((node) => {
    switch (node.type) {
      case 'text':
      case 'html':
        return [textRun(node.value ?? '', marks)];
      case 'inlineCode':
        return [
          textRun(
            node.value ?? '',
            mergeMarks(marks, {
              background: '#f1f5f9',
              color: '#334155',
              style: 'markdownInlineCode',
            })
          ),
        ];
      case 'break':
        return [textRun('\n', marks)];
      case 'strong':
        return renderMarkdownInline(node.children, mergeMarks(marks, { bold: true }));
      case 'emphasis':
        return renderMarkdownInline(node.children, mergeMarks(marks, { italics: true }));
      case 'delete':
        return renderMarkdownInline(node.children, mergeMarks(marks, { decoration: 'lineThrough' }));
      case 'link':
        return renderMarkdownInline(
          node.children,
          mergeMarks(marks, {
            color: '#2563eb',
            decoration: 'underline',
            link: node.url,
          })
        );
      default:
        if (node.children) return renderMarkdownInline(node.children, marks);
        return node.value ? [textRun(node.value, marks)] : [];
    }
  });
}

function renderMarkdownInlineText(nodes: MarkdownNode[] = [], style = 'bodyText'): ContentText {
  const inlineContent = renderMarkdownInline(nodes);
  return {
    text: inlineContent.length > 0 ? inlineContent : '',
    style,
  };
}

function renderMarkdownTable(node: MarkdownNode): Content[] {
  const rows = node.children ?? [];
  const columnCount = Math.max(
    0,
    ...rows.map((row) => row.children?.length ?? 0)
  );

  if (rows.length === 0 || columnCount === 0) return [];

  const body = rows.map((row, rowIndex) => {
    const cells = row.children ?? [];
    return Array.from({ length: columnCount }, (_, cellIndex) => {
      const cell = cells[cellIndex];
      return {
        ...renderMarkdownInlineText(
          cell?.children,
          rowIndex === 0 ? 'tableHeader' : 'tableCell'
        ),
        margin: MARKDOWN_TABLE_CELL_MARGIN,
      };
    });
  });

  return [
    {
      table: {
        headerRows: 1,
        widths: Array.from({ length: columnCount }, () => '*'),
        body,
      },
      layout: 'lightHorizontalLines',
      margin: [0, 4, 0, 10],
    },
  ];
}

function renderMarkdownListItem(node: MarkdownNode): Content {
  const blocks = renderMarkdownBlocks(node.children);

  if (typeof node.checked === 'boolean') {
    blocks.unshift({
      text: node.checked ? '[x]' : '[ ]',
      bold: true,
      style: 'bodyText',
      margin: [0, 0, 0, 2],
    });
  }

  if (blocks.length === 0) return { text: '', style: 'bodyText' };
  if (blocks.length === 1) return blocks[0];

  return {
    stack: blocks,
    margin: [0, 0, 0, 4],
  };
}

function renderMarkdownBlocks(nodes: MarkdownNode[] = []): Content[] {
  return nodes.flatMap((node) => {
    switch (node.type) {
      case 'root':
        return renderMarkdownBlocks(node.children);
      case 'paragraph':
        return [
          {
            ...renderMarkdownInlineText(node.children),
            margin: [0, 0, 0, 6],
          },
        ];
      case 'heading': {
        const depth = Math.min(Math.max(node.depth ?? 3, 1), 3);
        return [
          {
            ...renderMarkdownInlineText(node.children, `markdownHeading${depth}`),
            margin: [0, depth === 1 ? 8 : 6, 0, 4],
          },
        ];
      }
      case 'list': {
        const items = (node.children ?? []).map(renderMarkdownListItem);
        const listContent = node.ordered
          ? { ol: items, start: node.start }
          : { ul: items };

        return [
          {
            ...listContent,
            style: 'bodyText',
            margin: [0, 0, 0, 8],
          },
        ];
      }
      case 'blockquote':
        return [
          {
            stack: renderMarkdownBlocks(node.children),
            style: 'markdownQuote',
            margin: [8, 2, 0, 8],
          },
        ];
      case 'code':
        return [
          {
            text: node.value ?? '',
            style: 'markdownCodeBlock',
            margin: [0, 2, 0, 8],
          },
        ];
      case 'thematicBreak':
        return [
          {
            canvas: [
              {
                type: 'line',
                x1: 0,
                y1: 0,
                x2: 240,
                y2: 0,
                lineWidth: 1,
                lineColor: '#cbd5e1',
              },
            ],
            margin: [0, 6, 0, 8],
          },
        ];
      case 'table':
        return renderMarkdownTable(node);
      case 'html':
        return [
          {
            text: node.value ?? '',
            style: 'bodyText',
            margin: [0, 0, 0, 6],
          },
        ];
      default:
        if (node.children) return renderMarkdownBlocks(node.children);
        return node.value
          ? [
              {
                text: node.value,
                style: 'bodyText',
                margin: [0, 0, 0, 6],
              },
            ]
          : [];
    }
  });
}

function renderMarkdownContent(value: string | null | undefined, fallback = 'Not provided'): Content[] {
  const text = textValue(value, fallback);
  if (text === fallback) {
    return [{ text, style: 'bodyText' }];
  }

  const blocks = renderMarkdownBlocks([markdownTree(text)]);
  return blocks.length > 0 ? blocks : [{ text, style: 'bodyText' }];
}

function sectionWithFallback<T>(
  label: string,
  promise: Promise<T>,
  fallback: T
): Promise<ExportSection<T>> {
  return promise
    .then((data) => ({ data }))
    .catch((error: unknown) => ({
      data: fallback,
      error: `${label}: ${error instanceof Error ? error.message : String(error)}`,
    }));
}

function getRelatedUserIds(items: RelatedItem[]): string[] {
  const ids = new Set<string>();

  items.forEach((item) => {
    if ('assessorId' in item) ids.add(item.assessorId);
    if ('authorId' in item) ids.add(item.authorId);
    if ('endorserId' in item) ids.add(item.endorserId);
    if ('inviterId' in item) ids.add(item.inviterId);
    if ('inviteeId' in item) ids.add(item.inviteeId);
  });

  return [...ids];
}

async function fetchRelatedUsers(userIds: string[]): Promise<{
  users: Record<string, User>;
  errors: string[];
}> {
  const uniqueIds = [...new Set(userIds.filter(Boolean))];
  const results: UserLoadResult[] = await Promise.all(
    uniqueIds.map(async (userId) => {
      try {
        const user = await get<User>(`/users/${userId}`);
        return { userId, user };
      } catch (error) {
        return {
          userId,
          error: error instanceof Error ? error.message : String(error),
        };
      }
    })
  );

  return results.reduce(
    (acc, result) => {
      if ('user' in result) {
        acc.users[result.userId] = result.user;
      } else {
        acc.errors.push(`User ${result.userId}: ${result.error}`);
      }
      return acc;
    },
    { users: {} as Record<string, User>, errors: [] as string[] }
  );
}

export async function fetchPacketPdfExportData(packetId: string): Promise<PacketPdfExportData> {
  const packet = await get<PacketWithCandidate>(`/packets/${packetId}`);

  const [
    criteria,
    endorsements,
    concerns,
    invitations,
    blindedAssessments,
    unblindedAssessments,
    barRaiserAssessments,
    previousAttempts,
  ] = await Promise.all([
    sectionWithFallback('Criteria', get<Criterion[]>('/criteria', { packetId }), []),
    sectionWithFallback('Endorsements', get<Endorsement[]>('/endorsements', { packetId }), []),
    sectionWithFallback('Opportunities', get<Concern[]>('/concerns', { packetId }), []),
    sectionWithFallback('Invitations', get<Invitation[]>('/invitations', { packetId }), []),
    sectionWithFallback(
      'Blinded assessments',
      get<Assessment[]>('/assessments', { packetId, type: AssessmentType.BLINDED }),
      []
    ),
    sectionWithFallback(
      'Unblinded assessments',
      get<Assessment[]>('/assessments', { packetId, type: AssessmentType.UNBLINDED }),
      []
    ),
    sectionWithFallback(
      'Bar raiser assessments',
      get<Assessment[]>('/assessments', { packetId, type: AssessmentType.BAR_RAISER }),
      []
    ),
    packet.attempt && packet.attempt > 1
      ? sectionWithFallback(
          'Previous attempts',
          get<PacketWithCandidate[]>('/packets', {
            candidateId: packet.candidateId,
            year: packet.year,
            includeAttempts: 'true',
          }).then((attempts) =>
            attempts.filter(
              (attempt) =>
                attempt.id !== packet.id &&
                (attempt.attempt ?? 1) < (packet.attempt ?? 1)
            )
          ),
          []
        )
      : Promise.resolve({ data: [] }),
  ]);

  const userIds = [
    packet.managerId,
    packet.seniorLeaderId,
    ...(packet.managerIds ?? []),
    ...getRelatedUserIds([
      ...endorsements.data,
      ...concerns.data,
      ...invitations.data,
      ...blindedAssessments.data,
      ...unblindedAssessments.data,
      ...barRaiserAssessments.data,
    ]),
  ];
  const { users: relatedUsers, errors: userLoadErrors } = await fetchRelatedUsers(userIds);

  return {
    packet,
    criteria,
    endorsements,
    concerns,
    invitations,
    blindedAssessments,
    unblindedAssessments,
    barRaiserAssessments,
    previousAttempts,
    relatedUsers,
    userLoadErrors,
  };
}

function formatUser(
  users: Record<string, User>,
  userId: string | null | undefined,
  fallbackEmail?: string
): string {
  if (userId && users[userId]) {
    const user = users[userId];
    const name = user.name?.trim() || parseNameFromEmail(user.email) || user.email;
    return user.title ? `${name}, ${user.title} (${user.email})` : `${name} (${user.email})`;
  }

  if (fallbackEmail) {
    return parseNameFromEmail(fallbackEmail) || fallbackEmail;
  }

  return userId || 'Unknown';
}

function definitionCell([label, value]: DefinitionItem): TableCell {
  return {
    stack: [
      { text: label, style: 'definitionLabel' },
      { text: textValue(value), style: 'definitionValue' },
    ],
    margin: [0, 0, 10, 10],
  };
}

function renderDefinitionGrid(items: DefinitionItem[]): Content {
  const rows: TableCell[][] = [];

  for (let index = 0; index < items.length; index += 3) {
    const row = items.slice(index, index + 3).map(definitionCell);
    while (row.length < 3) row.push({ text: '' });
    rows.push(row);
  }

  return {
    table: {
      widths: ['*', '*', '*'],
      body: rows,
    },
    layout: 'noBorders',
    margin: [0, 16, 0, 8],
  };
}

function renderTextBlock(label: string, value: string | null | undefined): Content {
  return {
    stack: [
      { text: label, style: 'blockLabel' },
      ...renderMarkdownContent(value),
    ],
    margin: [0, 8, 0, 2],
  };
}

function renderOptionalTextBlock(label: string, value: string | null | undefined): Content[] {
  return hasText(value) ? [renderTextBlock(label, value)] : [];
}

function renderSectionError(error?: string): Content[] {
  return error
    ? [
        {
          text: error,
          style: 'sectionError',
          margin: [0, 4, 0, 8],
        },
      ]
    : [];
}

function renderEmptyState(label: string): Content {
  return {
    text: `No ${label} recorded.`,
    style: 'muted',
    margin: [0, 4, 0, 8],
  };
}

function renderSection(title: string, body: Content[]): Content[] {
  return [
    {
      text: title,
      style: 'sectionTitle',
      margin: [0, 18, 0, 8],
    },
    ...body,
  ];
}

function renderEntry(meta: string, body: Content[]): Content {
  return {
    stack: [
      {
        text: meta,
        style: 'entryMeta',
        margin: [0, 0, 0, 6],
      },
      ...body,
    ],
    margin: [0, 8, 0, 8],
  };
}

function tableText(value: string | number | null | undefined): TableCell {
  return {
    text: textValue(value),
    style: 'tableCell',
  };
}

function tableHeader(value: string): TableCell {
  return {
    text: value,
    style: 'tableHeader',
  };
}

function renderDataTable(headers: string[], rows: Array<Array<string | number | null | undefined>>): Content {
  return {
    table: {
      headerRows: 1,
      widths: headers.map(() => '*'),
      body: [
        headers.map(tableHeader),
        ...rows.map((row) => row.map(tableText)),
      ],
    },
    layout: 'lightHorizontalLines',
    margin: [0, 4, 0, 10],
  };
}

function renderCriteria(criteria: ExportSection<Criterion[]>): Content[] {
  const body: Content[] = [
    ...renderSectionError(criteria.error),
    ...(criteria.data.length === 0
      ? [renderEmptyState('criteria')]
      : criteria.data.map((criterion) =>
          renderEntry(formatEnum(criterion.criterionType), [
            renderTextBlock('Description', criterion.description),
            renderTextBlock('Evidence', criterion.evidence),
          ])
        )),
  ];

  return renderSection('Promotion Criteria', body);
}

function renderEndorsements(
  endorsements: ExportSection<Endorsement[]>,
  users: Record<string, User>
): Content[] {
  const body: Content[] = [
    ...renderSectionError(endorsements.error),
    ...(endorsements.data.length === 0
      ? [renderEmptyState('endorsements')]
      : endorsements.data.map((endorsement) =>
          renderEntry(formatUser(users, endorsement.endorserId), [
            renderTextBlock('Endorsement', endorsement.content),
          ])
        )),
  ];

  return renderSection('Endorsements', body);
}

function renderConcerns(concerns: ExportSection<Concern[]>, users: Record<string, User>): Content[] {
  const body: Content[] = [
    ...renderSectionError(concerns.error),
    ...(concerns.data.length === 0
      ? [renderEmptyState('opportunities')]
      : concerns.data.map((concern) =>
          renderEntry(formatUser(users, concern.authorId), [
            renderTextBlock('Opportunity', concern.content),
          ])
        )),
  ];

  return renderSection('Opportunities And Concerns', body);
}

function formatPercent(count: number, total: number): string {
  if (total === 0) return '0%';

  return `${Math.round((count / total) * 100)}%`;
}

function renderLegendItem(label: string, count: number, total: number, color: string): Content {
  const marker: CanvasElement[] = [
    {
      type: 'rect',
      x: 0,
      y: 2,
      w: 8,
      h: 8,
      r: 2,
      color,
      lineColor: color,
    },
  ];

  return {
    columns: [
      {
        width: 12,
        canvas: marker,
      },
      {
        width: 'auto',
        text: `${label} ${count} (${formatPercent(count, total)})`,
        style: 'chartLabel',
      },
    ],
    columnGap: 2,
  };
}

function renderAssessmentDecisionChart(assessments: Assessment[]): Content[] {
  const completedAssessments = assessments.filter(
    (assessment) => assessment.status === AssessmentStatus.COMPLETED
  );
  const promoteCount = completedAssessments.filter(
    (assessment) => assessment.decision === Decision.PROMOTE
  ).length;
  const deferCount = completedAssessments.filter(
    (assessment) => assessment.decision === Decision.DEFER
  ).length;
  const totalDecisionCount = promoteCount + deferCount;

  if (totalDecisionCount === 0) return [];

  const promoteWidth = (promoteCount / totalDecisionCount) * DECISION_CHART_WIDTH;
  const deferWidth = DECISION_CHART_WIDTH - promoteWidth;
  const chartElements: CanvasElement[] = [
    {
      type: 'rect',
      x: 0,
      y: 0,
      w: DECISION_CHART_WIDTH,
      h: DECISION_CHART_HEIGHT,
      r: 4,
      color: CHART_BACKGROUND_COLOR,
      lineColor: '#cbd5e1',
    },
  ];

  if (promoteWidth > 0) {
    chartElements.push({
      type: 'rect',
      x: 0,
      y: 0,
      w: promoteWidth,
      h: DECISION_CHART_HEIGHT,
      r: deferWidth === 0 ? 4 : 0,
      color: PROMOTE_COLOR,
      lineColor: PROMOTE_COLOR,
    });
  }

  if (deferWidth > 0) {
    chartElements.push({
      type: 'rect',
      x: promoteWidth,
      y: 0,
      w: deferWidth,
      h: DECISION_CHART_HEIGHT,
      r: promoteWidth === 0 ? 4 : 0,
      color: DEFER_COLOR,
      lineColor: DEFER_COLOR,
    });
  }

  return [
    {
      stack: [
        {
          text: 'Decision Summary',
          style: 'chartTitle',
          margin: [0, 0, 0, 4],
        },
        {
          canvas: chartElements,
          margin: [0, 0, 0, 6],
        },
        {
          columns: [
            renderLegendItem('Promote', promoteCount, totalDecisionCount, PROMOTE_COLOR),
            renderLegendItem('Defer', deferCount, totalDecisionCount, DEFER_COLOR),
            {
              width: '*',
              text: `${totalDecisionCount} completed decisions`,
              alignment: 'right',
              style: 'chartLabel',
            },
          ],
          columnGap: 14,
        },
      ],
      margin: [0, 6, 0, 12],
      unbreakable: true,
    },
  ];
}

function renderAssessments(
  title: string,
  assessments: ExportSection<Assessment[]>,
  users: Record<string, User>
): Content[] {
  const completedCount = assessments.data.filter(
    (item) => item.status === AssessmentStatus.COMPLETED
  ).length;
  const body: Content[] = [
    ...renderSectionError(assessments.error),
    ...(assessments.data.length === 0
      ? [renderEmptyState(`${title.toLowerCase()} reviews`)]
      : [
          {
            text: `${completedCount} of ${assessments.data.length} submitted.`,
            style: 'muted',
            margin: [0, 0, 0, 4] as [number, number, number, number],
          },
          ...renderAssessmentDecisionChart(assessments.data),
          ...assessments.data.map((assessment) =>
            renderEntry(formatUser(users, assessment.assessorId), [
              renderDefinitionGrid([
                ['Status', formatEnum(assessment.status)],
                ['Decision', formatEnum(assessment.decision)],
                ['Updated', formatDateTime(assessment.updatedAt)],
              ]),
              renderTextBlock('Justification', assessment.justification),
            ])
          ),
        ]),
  ];

  return renderSection(title, body);
}

function renderInvitations(
  invitations: ExportSection<Invitation[]>,
  users: Record<string, User>
): Content[] {
  const body: Content[] = [
    ...renderSectionError(invitations.error),
    invitations.data.length === 0
      ? renderEmptyState('invitations')
      : renderDataTable(
          ['Role', 'Invitee', 'Status', 'Invited', 'Expires'],
          invitations.data.map((invitation) => [
            formatEnum(invitation.invitedRole),
            formatUser(users, invitation.inviteeId, invitation.inviteeEmail),
            formatEnum(invitation.status),
            formatDate(invitation.createdAt),
            formatDate(invitation.expiresAt),
          ])
        ),
  ];

  return renderSection('Invitations And Participation', body);
}

function renderPreviousAttempts(previousAttempts: ExportSection<PacketWithCandidate[]>): Content[] {
  if (!previousAttempts.error && previousAttempts.data.length === 0) return [];

  const body: Content[] = [
    ...renderSectionError(previousAttempts.error),
    previousAttempts.data.length === 0
      ? renderEmptyState('previous attempts')
      : renderDataTable(
          ['Attempt', 'Status', 'Recommendation', 'Decision', 'Created'],
          previousAttempts.data.map((attempt) => [
            attempt.attempt ?? 1,
            getDistinctPacketStatusLabel(attempt.status),
            formatEnum(attempt.recommendation),
            formatEnum(attempt.decision),
            formatDate(attempt.createdAt),
          ])
        ),
  ];

  return renderSection('Previous Attempts', body);
}

function renderAccessIssues(data: PacketPdfExportData): Content[] {
  const issues = [
    data.criteria.error,
    data.endorsements.error,
    data.concerns.error,
    data.invitations.error,
    data.blindedAssessments.error,
    data.unblindedAssessments.error,
    data.barRaiserAssessments.error,
    data.previousAttempts.error,
    ...data.userLoadErrors,
  ].filter((issue): issue is string => Boolean(issue));

  if (issues.length === 0) return [];

  return renderSection('Export Limitations', [
    {
      ul: issues.map((issue) => ({
        text: issue,
        style: 'bodyText',
      })),
      margin: [0, 4, 0, 8],
    },
  ]);
}

export function buildPacketPdfDocumentDefinition(
  data: PacketPdfExportData,
  generatedAt: Date = new Date()
): TDocumentDefinitions {
  const { packet, relatedUsers } = data;
  const managerNames = (packet.managerIds?.length ? packet.managerIds : [packet.managerId])
    .map((managerId) => formatUser(relatedUsers, managerId))
    .join('; ');

  const content: Content[] = [
    { text: `${packet.candidateName} Promotion Packet`, style: 'title' },
    {
      text: `Generated ${formatDateTime(generatedAt)}. This export reflects the packet information visible to the exporting user at generation time.`,
      style: 'subtitle',
      margin: [0, 4, 0, 0],
    },
    renderDefinitionGrid([
      ['Candidate', packet.candidateName],
      ['Current title', packet.currentTitle],
      ['Target title', packet.targetTitle],
      ['Assessment year', packet.year],
      ['Attempt', packet.attempt ?? 1],
      ['Status', getDistinctPacketStatusLabel(packet.status)],
      ['Visibility', formatEnum(packet.visibility)],
      ['Time in role', packet.timeInRole],
      ['Last promoted', formatDate(packet.lastPromotedDate)],
      ['Manager / co-managers', managerNames],
      ['Senior leader', formatUser(relatedUsers, packet.seniorLeaderId)],
      ['Created', formatDate(packet.createdAt)],
      ['Updated', formatDate(packet.updatedAt)],
      ['Recommendation', formatEnum(packet.recommendation)],
      ['Ratification', formatEnum(packet.ratification)],
      ['Final decision', formatEnum(packet.decision)],
    ]),
    renderTextBlock('Manager Summary', packet.summary),
    ...(packet.decision ? renderOptionalTextBlock('Decision Justification', packet.decisionJustification) : []),
    ...(packet.ratification === Ratification.OVERRULED
      ? renderOptionalTextBlock('Overrule Justification', packet.overruleJustification)
      : []),
    ...(packet.status === PacketStatus.WITHDRAWN
      ? renderOptionalTextBlock('Withdrawal Justification', packet.withdrawalJustification)
      : []),
    ...renderPreviousAttempts(data.previousAttempts),
    ...renderCriteria(data.criteria),
    ...renderEndorsements(data.endorsements, relatedUsers),
    ...renderConcerns(data.concerns, relatedUsers),
    ...renderAssessments('Blinded Assessments', data.blindedAssessments, relatedUsers),
    ...renderAssessments('Unblinded Assessments', data.unblindedAssessments, relatedUsers),
    ...renderAssessments('Bar Raiser Review', data.barRaiserAssessments, relatedUsers),
    ...renderInvitations(data.invitations, relatedUsers),
    ...renderAccessIssues(data),
  ];

  return {
    content,
    defaultStyle: {
      color: '#17202a',
      font: 'Roboto',
      fontSize: 10,
      lineHeight: 1.2,
    },
    footer: (currentPage, pageCount) => ({
      text: `Page ${currentPage} of ${pageCount}`,
      alignment: 'right',
      color: '#64748b',
      fontSize: 8,
      margin: [0, 0, 36, 0],
    }),
    info: {
      title: `${packet.candidateName} promotion packet`,
      subject: 'Promotion packet PDF export',
      creator: 'Promo by Tebra',
    },
    pageMargins: [36, 36, 36, 42],
    pageSize: 'LETTER',
    styles: {
      title: {
        bold: true,
        color: '#111827',
        fontSize: 20,
      },
      subtitle: {
        color: '#4b5563',
        fontSize: 9,
      },
      definitionLabel: {
        bold: true,
        color: '#64748b',
        fontSize: 7,
      },
      definitionValue: {
        color: '#17202a',
        fontSize: 10,
      },
      sectionTitle: {
        bold: true,
        color: '#111827',
        fontSize: 14,
      },
      blockLabel: {
        bold: true,
        color: '#111827',
        fontSize: 9,
      },
      bodyText: {
        color: '#17202a',
        fontSize: 10,
      },
      markdownHeading1: {
        bold: true,
        color: '#111827',
        fontSize: 13,
      },
      markdownHeading2: {
        bold: true,
        color: '#1f2937',
        fontSize: 12,
      },
      markdownHeading3: {
        bold: true,
        color: '#334155',
        fontSize: 11,
      },
      markdownInlineCode: {
        color: '#334155',
        fontSize: 9,
      },
      markdownCodeBlock: {
        background: '#f1f5f9',
        color: '#334155',
        fontSize: 8,
      },
      markdownQuote: {
        color: '#475569',
        italics: true,
      },
      entryMeta: {
        bold: true,
        color: '#334155',
        fontSize: 10,
      },
      muted: {
        color: '#64748b',
        fontSize: 9,
      },
      sectionError: {
        color: '#9a3412',
        fontSize: 9,
      },
      tableHeader: {
        bold: true,
        color: '#475569',
        fontSize: 8,
      },
      tableCell: {
        color: '#17202a',
        fontSize: 9,
      },
      chartTitle: {
        bold: true,
        color: '#111827',
        fontSize: 9,
      },
      chartLabel: {
        color: '#475569',
        fontSize: 8,
      },
    },
  };
}

export function buildPacketPdfFilename(packet: PacketWithCandidate): string {
  const candidate = packet.candidateName
    .trim()
    .replace(/[^a-z0-9]+/gi, '-')
    .replace(/^-+|-+$/g, '')
    .toLowerCase();
  const year = packet.year || new Date().getFullYear().toString();

  return `${candidate || 'candidate'}-promotion-packet-${year}.pdf`;
}

async function defaultDownloadPdf(
  documentDefinition: TDocumentDefinitions,
  filename: string
): Promise<void> {
  const pdfMake = await loadPdfMake();
  await pdfMake.createPdf(documentDefinition).download(filename);
}

export async function exportPacketToPdf(
  packetId: string,
  options: ExportPacketPdfOptions = {}
): Promise<void> {
  let data: PacketPdfExportData;
  try {
    data = await fetchPacketPdfExportData(packetId);
  } catch (error) {
    if (isNetworkFetchError(error)) {
      throw new Error('Could not reach the server while exporting. Check your connection and try again.');
    }
    throw error;
  }

  const documentDefinition = buildPacketPdfDocumentDefinition(data, options.now);
  const filename = buildPacketPdfFilename(data.packet);
  await (options.downloadPdf ?? defaultDownloadPdf)(documentDefinition, filename);
}
